<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { uid } from '@sg-core/resources/assets/js/utils';

import { PhxsgButton } from '../../Atoms/Button';
import { Collapse } from '../../Atoms/Collapse';
import { PhxsgContainer } from '../../Atoms/Container';
import PhxsgRichText from '../../Atoms/RichText/phxsg-rich-text.vue';
import { PhxsgSection, type PhxsgSectionTheme } from '../../Atoms/Section';

export interface PhxsgPromoBannerOptionsProps {
  duration?: number;
}

export interface PhxsgPromoBannerProps {
  id?: string;
  theme?: PhxsgSectionTheme;
  items?: string[];
  closable?: boolean;
  expanded?: boolean;
  options?: PhxsgPromoBannerOptionsProps;
  a11y?: {
    closeLabel: string;
  };
}

const {
  id = uid('phxsg-promo-banner'),
  theme = 'blue-80',
  items = [],
  closable = false,
  expanded = true,
  options = {},
  a11y = { closeLabel: 'Close' },
} = defineProps<PhxsgPromoBannerProps>();

const rootRef = ref<HTMLElement | null>(null);
const slideshowIndex = ref<number>(0);
const collapseInstance = ref<InstanceType<typeof Collapse> | null>(null);
const interval = ref<ReturnType<typeof setInterval> | null>(null);

onMounted(() => {
  if (closable) {
    collapseInstance.value = new Collapse(rootRef.value as HTMLElement);
  }

  if (items.length <= 1) {
    return;
  }

  interval.value = setInterval(() => {
    slideshowIndex.value = slideshowIndex.value === items.length - 1 ? 0 : slideshowIndex.value + 1;
  }, options.duration || 5000);
});

onUnmounted(() => {
  if (interval.value) {
    clearInterval(interval.value);
    interval.value = null;
  }

  collapseInstance.value?.destroy();
});

defineEmits(['close']);
</script>

<template>
  <PhxsgSection v-if="items.length || $slots.default" :theme="theme">
    <div
      :id="id"
      ref="rootRef"
      :class="['phxsg-promo-banner', { 'phxsg-collapse': closable }, { 'phxsg-is-expanded': expanded }]"
      role="presentation"
    >
      <PhxsgContainer size="md" class="phxsg-promo-banner__center">
        <div class="phxsg-promo-banner__inner">
          <Transition v-if="items && !$slots.default" name="fade" mode="out-in">
            <PhxsgRichText
              v-if="items.length"
              :key="`promo-banner-item-${slideshowIndex}`"
              :html="items[slideshowIndex]"
              class="phxsg-promo-banner__item"
              centered
            />
          </Transition>

          <slot v-else />
        </div>
        <PhxsgButton
          v-if="closable"
          :icon-only="true"
          :icon="{ end: { id: 'close', size: 'xs' } }"
          data-toggle="collapse"
          :data-target="`#${id}`"
          aria-expanded
          :aria-label="a11y.closeLabel"
          :aria-controls="id"
          class="phxsg-promo-banner__close"
          @click="$emit('close')"
        />
      </PhxsgContainer>
    </div>
  </PhxsgSection>
</template>

<style src="./promo-banner.css" />
<style src="../../Atoms/Collapse/collapse.css" />
